import type { Fund as DdbFund } from "@etfsite/db/types";

/**
 * The current deploy environment, injected at build time via Vite's `VITE_ENV`.
 * Falls back to `"development"` for local dev when the var is unset.
 */
export const CURRENT_ENV = (import.meta.env.VITE_ENV ?? "development") as
  | "development"
  | "staging"
  | "production";

/**
 * Returns the boolean field on a fund record that gates visibility in the
 * current environment. e.g. `"isLiveDevelopment"` on dev.
 */
function liveFlagFor(env: typeof CURRENT_ENV): keyof DdbFund {
  return env === "production"
    ? "isLiveProduction"
    : env === "staging"
      ? "isLiveStaging"
      : "isLiveDevelopment";
}

/**
 * True when a fund should be visible (card, list, detail page, link).
 *
 * A fund is "live" in this env iff its `isLive{Env}` field is `true`.
 * `undefined` is treated as `false` (default-hidden — admin must opt in).
 *
 * This is the SINGLE source of truth — every consumer that filters funds
 * must use this helper so the rules can't drift between pages.
 */
export function isFundLiveHere(fund: DdbFund): boolean {
  const field = liveFlagFor(CURRENT_ENV);
  return fund[field] === true;
}
